import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Plus, Trash2, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  property_id: "", room_id: "", period: new Date().toISOString().slice(0, 7),
  previous_reading: 0, current_reading: 0,
  previous_reading_2: 0, current_reading_2: 0,
  rate_per_unit: 8, maintenance_amount: 0, notes: "", photo_path: null,
};

function MeterInputs({ title, prevKey, currKey, units, form, setForm, required, testidPrefix }) {
  return (
    <div className="border border-slate-200 p-5 bg-slate-50">
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">{title}</div>
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Initial reading</label>
          <input required={required} type="number" min="0" data-testid={`${testidPrefix}-prev`}
                 className="border border-slate-300 px-4 py-2.5 w-full"
                 value={form[prevKey]} onChange={(e) => setForm({ ...form, [prevKey]: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Final reading</label>
          <input required={required} type="number" min="0" data-testid={`${testidPrefix}-curr`}
                 className="border border-slate-300 px-4 py-2.5 w-full"
                 value={form[currKey]} onChange={(e) => setForm({ ...form, [currKey]: e.target.value })} />
        </div>
        <div className="flex items-end">
          <div className="text-sm">Units consumed: <span className="font-mono font-semibold">{units}</span></div>
        </div>
      </div>
    </div>
  );
}

function MeterForm({ form, setForm, props, rooms, onSubmit, onCancel, onUploadPhoto }) {
  const filteredRooms = rooms.filter((r) => r.property_id === form.property_id);

  // Preview of computed values for the form
  const u1 = Math.max(0, Number(form.current_reading) - Number(form.previous_reading));
  const u2 = Math.max(0, Number(form.current_reading_2) - Number(form.previous_reading_2));
  const totalUnits = u1 + u2;
  const elec = totalUnits * Number(form.rate_per_unit);
  const formTotal = elec + Number(form.maintenance_amount);

  return (
    <form onSubmit={onSubmit} className="bg-white border border-slate-200 p-6 space-y-5" data-testid="meter-form">
      <div className="grid md:grid-cols-3 gap-4">
        <select required className="border border-slate-300 px-4 py-2.5" value={form.property_id} data-testid="meter-property"
                onChange={(e) => setForm({ ...form, property_id: e.target.value, room_id: "" })}>
          <option value="">Select property…</option>
          {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className="border border-slate-300 px-4 py-2.5" value={form.room_id}
                onChange={(e) => setForm({ ...form, room_id: e.target.value })}>
          <option value="">Whole property (optional room)</option>
          {filteredRooms.map((r) => <option key={r.id} value={r.id}>{r.room_number}</option>)}
        </select>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Period</label>
          <input required type="month" data-testid="meter-period" className="border border-slate-300 px-4 py-2.5 w-full"
                 value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
        </div>
      </div>

      <MeterInputs title="Meter 1" prevKey="previous_reading" currKey="current_reading"
                   units={u1} form={form} setForm={setForm} required testidPrefix="meter" />
      <MeterInputs title="Meter 2 (optional)" prevKey="previous_reading_2" currKey="current_reading_2"
                   units={u2} form={form} setForm={setForm} testidPrefix="meter-2" />

      {/* Rate + Maintenance + Notes + Photo */}
      <div className="grid md:grid-cols-4 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Rate per unit (₹)</label>
          <input required type="number" min="0" step="0.01" data-testid="meter-rate"
                 className="border border-slate-300 px-4 py-2.5 w-full"
                 value={form.rate_per_unit} onChange={(e) => setForm({ ...form, rate_per_unit: e.target.value })} />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Maintenance (₹)</label>
          <input type="number" min="0" step="0.01" data-testid="meter-maint"
                 className="border border-slate-300 px-4 py-2.5 w-full"
                 value={form.maintenance_amount} onChange={(e) => setForm({ ...form, maintenance_amount: e.target.value })} />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Notes</label>
          <input className="border border-slate-300 px-4 py-2.5 w-full"
                 value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Meter box photo</label>
          <label className="w-full border border-slate-300 px-4 py-2.5 bg-slate-50 hover:bg-slate-100 cursor-pointer inline-flex items-center gap-2 text-sm">
            <Upload className="h-3.5 w-3.5" />
            {form.photo_path ? "Replace photo" : "Choose photo"}
            <input type="file" className="hidden" accept=".png,.jpg,.jpeg"
                   data-testid="meter-photo"
                   onChange={(e) => e.target.files[0] && onUploadPhoto(e.target.files[0])} />
          </label>
          {form.photo_path && <div className="text-xs text-green-700 mt-1">✓ Uploaded</div>}
        </div>
      </div>

      {/* Computed totals preview */}
      <div className="border-t border-slate-200 pt-4 flex items-center justify-between flex-wrap gap-2 text-sm">
        <div className="space-x-4">
          <span>Total units: <b className="font-mono">{totalUnits}</b></span>
          <span>Electricity: <b className="font-mono">₹{elec.toLocaleString("en-IN")}</b></span>
          <span>Maintenance: <b className="font-mono">₹{Number(form.maintenance_amount).toLocaleString("en-IN")}</b></span>
        </div>
        <div className="font-display text-xl font-bold">Total: ₹{formTotal.toLocaleString("en-IN")}</div>
      </div>

      <div className="flex gap-3">
        <button data-testid="meter-save" className="bg-slate-900 text-white px-6 py-2.5">Save</button>
        <button type="button" onClick={onCancel} className="bg-slate-100 px-6 py-2.5">Cancel</button>
      </div>
    </form>
  );
}

function MeterTable({ items, propName, roomName, onMarkPaid, onRemove }) {
  return (
    <div className="bg-white border border-slate-200 overflow-x-auto">
      {items.length === 0 ? (
        <div className="p-12 text-center text-slate-500" data-testid="meters-empty">No readings logged yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="px-4 py-3">Period</th>
              <th className="px-4 py-3">Property / Room</th>
              <th className="px-4 py-3">Prev #1</th>
              <th className="px-4 py-3">Curr #1</th>
              <th className="px-4 py-3">Prev #2</th>
              <th className="px-4 py-3">Curr #2</th>
              <th className="px-4 py-3">Units</th>
              <th className="px-4 py-3">Rate</th>
              <th className="px-4 py-3">Maintenance (₹)</th>
              <th className="px-4 py-3">Total (₹)</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => {
              const totalUnitsRow = Number(m.units || 0) + Number(m.units_2 || 0);
              return (
                <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono">{m.period}</td>
                  <td className="px-4 py-3 text-slate-600">{propName(m.property_id)}{m.room_id ? ` · ${roomName(m.room_id)}` : ""}</td>
                  <td className="px-4 py-3 font-mono">{m.previous_reading}</td>
                  <td className="px-4 py-3 font-mono">{m.current_reading}</td>
                  <td className="px-4 py-3 font-mono text-slate-500">{m.previous_reading_2 || 0}</td>
                  <td className="px-4 py-3 font-mono text-slate-500">{m.current_reading_2 || 0}</td>
                  <td className="px-4 py-3 font-mono">{totalUnitsRow}</td>
                  <td className="px-4 py-3 font-mono">₹{m.rate_per_unit}</td>
                  <td className="px-4 py-3 font-mono">₹{Number(m.maintenance_amount || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 font-mono font-semibold">₹{Number(m.amount).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    {!m.paid && (
                      <button onClick={() => onMarkPaid(m.id)} title="Mark paid"
                              data-testid={`meter-paid-${m.id}`}
                              className="text-green-700 hover:bg-green-50 p-2"><CheckCircle2 className="h-4 w-4" /></button>
                    )}
                    <button onClick={() => onRemove(m.id)} className="text-red-600 hover:bg-red-50 p-2"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Meters() {
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([api.get("/meter-readings"), api.get("/properties"), api.get("/rooms")]);
    setItems(a.data); setProps(b.data); setRooms(c.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        previous_reading: Number(form.previous_reading),
        current_reading: Number(form.current_reading),
        previous_reading_2: Number(form.previous_reading_2),
        current_reading_2: Number(form.current_reading_2),
        rate_per_unit: Number(form.rate_per_unit),
        maintenance_amount: Number(form.maintenance_amount),
      };
      if (!payload.room_id) delete payload.room_id;
      await api.post("/meter-readings", payload);
      toast.success("Reading saved");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete reading?")) return;
    await api.delete(`/meter-readings/${id}`); load();
  };

  const markPaid = async (id) => {
    await api.post(`/meter-readings/${id}/mark-paid`); toast.success("Marked paid"); load();
  };

  const uploadPhoto = async (file) => {
    if (file.size > 5 * 1024 * 1024) { toast.error("File > 5MB"); return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("category", "meter");
    try {
      const r = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm({ ...form, photo_path: r.data.path });
      toast.success("Meter photo uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const propName = (id) => props.find((p) => p.id === id)?.name || "—";
  const roomName = (id) => rooms.find((r) => r.id === id)?.room_number || "—";

  return (
    <div className="space-y-6" data-testid="meters-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Utilities</div>
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter">Electricity meters</h1>
          <p className="text-sm text-slate-500 mt-2">Two meters supported per reading + a separate maintenance charge.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="add-meter-btn"
                disabled={props.length === 0}
                className="bg-blue-600 text-white px-5 py-3 hover:bg-blue-700 inline-flex items-center gap-2 disabled:opacity-50">
          <Plus className="h-4 w-4" /> New reading
        </button>
      </div>

      {open && (
        <MeterForm form={form} setForm={setForm} props={props} rooms={rooms}
                   onSubmit={submit} onCancel={() => { setOpen(false); setForm(EMPTY_FORM); }}
                   onUploadPhoto={uploadPhoto} />
      )}

      <MeterTable items={items} propName={propName} roomName={roomName}
                  onMarkPaid={markPaid} onRemove={remove} />
    </div>
  );
}
